import { supabase } from './client';

export interface Shop {
  id: string;
  name: string;
  description: string;
  average_rating: number | null;
  latitude: number | null;
  longitude: number | null;
  image_url: string | null;
  address: string;
  // Removed review_count and category_name from interface for now due to schema issues
  review_count: number | null;
}

export interface Service {
  id: string;
  shop_id: string;
  category_id: string;
  name: string;
  duration_value: number; // Updated from duration_minutes
  duration_type: string; // Added duration_type
  price: number;
  created_at: string; // TIMESTAMPTZ
  description: string;
}

export interface Review {
  id: string;
  shop_id: string;
  rating: number;
  created_at: string;
}

export async function getShopById(shopId: string): Promise<Shop | null> {
  const { data: shopData, error: shopError } = await supabase
    .from('shops')
    .select('id, name, description, average_rating, latitude, longitude, image_url, address') // Only selecting existing columns
    .eq('id', shopId)
    .single();

  if (shopError) {
    console.error(`Error fetching shop with ID ${shopId}:`, shopError.message);
    return null;
  }

  if (shopData) {
    // Return shopData directly without adding review_count or category_name for now
    // These will be handled as placeholders in the UI or fetched differently if schema is confirmed
    return shopData as Shop;
  }

  return null;
}

export async function getServicesByShopId(shopId: string): Promise<Service[] | null> {
  const { data, error } = await supabase
    .from('services')
    .select('id, shop_id, category_id, name, duration_value, duration_type, price, created_at, description')
    .eq('shop_id', shopId);

  if (error) {
    console.error(`Error fetching services for shop with ID ${shopId}:`, error.message);
    return null;
  }

  return data as Service[];
}

export interface AvailableSlot {
  date: string; // YYYY-MM-DD
  dayOfWeek: string; // e.g., "Mon"
  slotsAvailable: number;
  isClosed: boolean;
}

export interface AvailableTime {
  formattedTime: string; // e.g., "09:00 AM"
  rawStartTime: string; // e.g., "HH:MM:SS" from RPC result
  isBooked: boolean;
  providerId: string; // Added providerId
}

export interface AvailableServiceRpcResult {
  shop_id: string;
  shop_name: string;
  shop_address: string;
  shop_latitude: number;
  shop_longitude: number;
  service_id: string;
  service_name: string;
  service_description: string;
  service_price: number;
  service_duration_value: number;
  service_duration_type: string;
  provider_id: string;
  provider_name: string;
  available_date: string; // DATE
  available_start_time: string; // TIME
  available_end_time: string; // TIME
  distance_km: number;
}

export async function getAvailableSlots(shopId: string, serviceId: string): Promise<AvailableSlot[] | null> {
  console.log(`Fetching available slots for shopId: ${shopId}, serviceId: ${serviceId}`);

  try {
    // Fetch service details to get its name
    const { data: serviceData, error: serviceError } = await supabase
      .from('services')
      .select('name')
      .eq('id', serviceId)
      .single();

    if (serviceError || !serviceData) {
      console.error(`Error fetching service name for ID ${serviceId}:`, serviceError?.message);
      return null;
    }

    const serviceName = serviceData.name;

    // We need to call get_available_services for a range of dates
    const today = new Date();
    const datesToCheck: string[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      datesToCheck.push(date.toISOString().split('T')[0]); // YYYY-MM-DD
    }

    const allAvailableServices: AvailableServiceRpcResult[] = [];

    // Call get_available_services for the date range
    const { data, error } = await supabase.rpc('get_available_services', {
      user_latitude: null, // Not available in this context
      user_longitude: null, // Not available in this context
      p_service_name: serviceName,
      p_start_date: datesToCheck[0],
      p_end_date: datesToCheck[datesToCheck.length - 1],
      p_desired_time: null,
      p_shop_name: null, // We are filtering by shop_id implicitly by fetching services for a shop
      p_max_distance_km: null,
      p_limit: null,
      p_offset: null,
    });

    if (error) {
      console.error(`Error fetching available services:`, error.message);
      return null;
    }

    if (data) {
      // Filter data by the specific shopId
      const filteredData = (data as AvailableServiceRpcResult[]).filter(item => item.shop_id === shopId);
      allAvailableServices.push(...filteredData);
    }

    // Process allAvailableServices to aggregate by date and count available slots
    const aggregatedSlots: { [date: string]: { count: number; dayOfWeek: string; isClosed: boolean } } = {};
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    for (const slot of allAvailableServices) {
      const slotDate = slot.available_date;
      const dateObj = new Date(slotDate);
      const dayOfWeek = dayNames[dateObj.getDay()];

      if (!aggregatedSlots[slotDate]) {
        aggregatedSlots[slotDate] = { count: 0, dayOfWeek, isClosed: false };
      }
      aggregatedSlots[slotDate].count++;
    }

    // Convert to AvailableSlot[] format
    const result: AvailableSlot[] = datesToCheck.map(date => {
      const dateObj = new Date(date);
      const dayOfWeek = dayNames[dateObj.getDay()];
      const slots = aggregatedSlots[date];
      return {
        date: date,
        dayOfWeek: dayOfWeek,
        slotsAvailable: slots?.count || 0,
        isClosed: !slots || slots.count === 0,
      };
    });

    return result;

  } catch (err) {
    console.error('Exception fetching available slots:', err);
    return null;
  }
}

export interface Booking {
  id: string;
  customer_id: string;
  shop_id: string;
  service_id: string;
  provider_id: string;
  start_time: string; // TIMESTAMPTZ
  created_at: string; // TIMESTAMPTZ
  shops: {
    name: string;
    address: string;
    image_url: string | null;
  } | null; // Can be null if join fails
  services: {
    name: string;
    duration_value: number;
    duration_type: string;
    price: number;
  } | null; // Can be null if join fails
}

export async function getUserBookings(customerId: string): Promise<Booking[] | null> {
  const { data, error } = await supabase
    .from('bookings')
    .select(`
      id,
      customer_id,
      shop_id,
      service_id,
      provider_id,
      start_time,
      created_at,
      shops (
        name,
        address,
        image_url
      ),
      services (
        name,
        duration_value,
        duration_type,
        price
      )
    `)
    .eq('customer_id', customerId)
    .order('start_time', { ascending: false }); // Order by newest bookings first

  if (error) {
    console.error(`Error fetching bookings for customer ${customerId}:`, error.message);
    return null;
  }

  // Log the raw data to help debug why shop/service info might be missing
  console.log('Raw bookings data:', data);

  return data as unknown as Booking[];
}

export async function getAvailableTimes(shopId: string, serviceId: string, date: string): Promise<AvailableTime[] | null> {
  console.log(`Fetching available times for shopId: ${shopId}, serviceId: ${serviceId}, date: ${date}`);

  try {
    // Fetch service details to get its name
    const { data: serviceData, error: serviceError } = await supabase
      .from('services')
      .select('name')
      .eq('id', serviceId)
      .single();

    if (serviceError || !serviceData) {
      console.error(`Error fetching service name for ID ${serviceId}:`, serviceError?.message);
      return null;
    }

    const serviceName = serviceData.name;

    // Call get_available_services for the specific date and service
    const { data, error } = await supabase.rpc('get_available_services', {
      user_latitude: null, // Not available in this context
      user_longitude: null, // Not available in this context
      p_service_name: serviceName,
      p_start_date: date,
      p_end_date: date,
      p_desired_time: null, // We want all times for the day
      p_shop_name: null, // We are filtering by shop_id implicitly
      p_max_distance_km: null,
      p_limit: null,
      p_offset: null,
    });

    if (error) {
      console.error(`Error fetching available services for times:`, error.message);
      return null;
    }

    const availableTimes: AvailableTime[] = [];
    if (data) {
      const filteredData = (data as AvailableServiceRpcResult[]).filter(item => item.shop_id === shopId && item.service_id === serviceId && item.available_date === date);

      // Extract unique times and their associated providerId
      const uniqueTimes: { [time: string]: AvailableTime } = {};
      for (const item of filteredData) {
        const startTime = new Date(`${item.available_date}T${item.available_start_time}Z`);
        const formattedTime = startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

        if (!uniqueTimes[formattedTime]) {
          uniqueTimes[formattedTime] = {
            formattedTime: formattedTime,
            rawStartTime: item.available_start_time, // Store the raw time string from RPC
            isBooked: false, // If it's returned by get_available_services, it's available
            providerId: item.provider_id, // Take the first provider for this time slot
          };
        }
      }
      availableTimes.push(...Object.values(uniqueTimes));
    }

    // Sort times chronologically
    availableTimes.sort((a, b) => {
      const timeA = new Date(`2000/01/01 ${a.formattedTime}`);
      const timeB = new Date(`2000/01/01 ${b.formattedTime}`);
      return timeA.getTime() - timeB.getTime();
    });

    return availableTimes;

  } catch (err) {
    console.error('Exception fetching available times:', err);
    return null;
  }
}


export async function bookAppointment(
  customerId: string,
  shopId: string,
  serviceId: string,
  providerId: string,
  startTime: string // TIMESTAMPTZ string
): Promise<string | null> {
  try {
    const { data, error } = await supabase.rpc('book_slot', {
      p_customer_id: customerId,
      p_shop_id: shopId,
      p_service_id: serviceId,
      p_provider_id: providerId,
      p_start_time: startTime,
    });

    if (error) {
      console.error('Error booking slot:', error.message);
      return null;
    }

    return data as string; // Assuming the function returns the booking ID
  } catch (err) {
    console.error('Exception during slot booking:', err);
    return null;
  }
}

export async function getReviewCountByShopId(shopId: string): Promise<number | null> {
  const { count, error } = await supabase
    .from('ratings')
    .select('*', { count: 'exact' })
    .eq('shop_id', shopId);

  if (error) {
    console.error(`Error fetching review count for shop with ID ${shopId}:`, error.message);
    return null;
  }

  return count;
}

export async function getReviewsByShopId(shopId: string): Promise<Review[] | null> {
  const { data, error } = await supabase
    .from('ratings')
    .select('id, shop_id, rating, created_at') // Removed user_id and comment
    .eq('shop_id', shopId)
    .order('created_at', { ascending: false }); // Order by newest reviews first

  if (error) {
    console.error(`Error fetching reviews for shop with ID ${shopId}:`, error.message);
    return null;
  }

  return data as Review[];
}

// Haversine formula to calculate distance between two points on Earth
const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371; // Radius of Earth in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in kilometers
};

export async function getCategories() {
  const { data, error } = await supabase
    .from('category')
    .select('id, name');

  if (error) {
    console.error('Error fetching categories:', error);
    return null;
  }

  return data;
}

export async function getShopsByCategory(
  categoryId: string,
  userLatitude: number | null,
  userLongitude: number | null
): Promise<Shop[] | null> {
  try {
    // Fetch services filtered by category_id and join with shops table
    const { data, error } = await supabase
      .from('services')
      .select(`
        category_id,
        shops!services_shop_id_fkey (
          id,
          name,
          description,
          average_rating,
          latitude,
          longitude,
          image_url
        )
      `)
      .eq('category_id', categoryId); // Filter on the services table's category_id

    if (error) {
      console.error('Error fetching shops by category:', error);
      return null;
    }

    // Extract unique shops from the joined data
    const uniqueShops: { [key: string]: Shop } = {};
    if (data) {
      for (const serviceData of data) {
        if (serviceData.shops) {
          const shop = serviceData.shops as unknown as Shop;
          uniqueShops[shop.id] = shop; // Use shop ID as key to ensure uniqueness
        }
      }
    }

    let shops: Shop[] = Object.values(uniqueShops);

    // Sort shops by distance if user location is available
    if (userLatitude !== null && userLongitude !== null) {
      shops.sort((a, b) => {
        // Ensure shop latitude and longitude are not null before calculating distance
        const latA = a.latitude ?? 0; // Default to 0 if null, or handle error appropriately
        const lonA = a.longitude ?? 0;
        const latB = b.latitude ?? 0;
        const lonB = b.longitude ?? 0;

        const distanceA = haversineDistance(userLatitude, userLongitude, latA, lonA);
        const distanceB = haversineDistance(userLatitude, userLongitude, latB, lonB);
        return distanceA - distanceB;
      });
    }

    return shops;
  } catch (e) {
    console.error('Error in getShopsByCategory:', e);
    return null;
  }
}

export async function getServicesByCategory(categoryId: string): Promise<Service[] | null> {
  const { data, error } = await supabase
    .from('services')
    .select('id, shop_id, category_id, name, duration_value, duration_type, price, created_at, description') // Updated select statement
    .eq('category_id', categoryId);

  if (error) {
    console.error(`Error fetching services for category ${categoryId}:`, error.message, error.details, error.hint, error.code);
    return null;
  }

  return data as Service[];
}
